import { atom, useAtomValue, useSetAtom } from 'jotai';
import { secureHttpClient } from '../restClient';
import { appDataByMerchant } from './configManager';
import { addToContext } from './contextManager';
import logger from './loggerManager';
import { StoreId } from './storeManager';
import { saveToUserSession } from './userSessionStorageManager';
import { OtbSupportForWaterfall } from './lendersConfigurationManager';

import { useEffect } from 'react';
import { atomWithReset, useResetAtom } from 'jotai/utils';
import { ConsumerDetails } from '@chargeafter/payment-sdk';
interface ConsumerAccountsData {
  accounts: ConsumerAccountData[];
  selectedAccount: ConsumerAccountData;
  validAccounts: ConsumerAccountData[];
}

export type ConsumerAccountData = {
  applicationId: string;
  availableAmount?: number;
  checkoutMinimumAmount?: number;
  financialProductType?: string;
  currency?: string;
  estimated: boolean;
  lenderId: string;
  lenderName: string;
  originalAmount: number;
  status?: Status;
  updated: string;
};

export type Status =
  | 'Prequalified'
  | 'CheckoutInProgress'
  | 'PendingConsumer'
  | 'PendingLender'
  | 'Approved'
  | 'Authorized'
  | 'Declined'
  | 'AUTHORIZE'
  | 'SETTLE'
  | 'REFUND'
  | 'VOID';

const ConsumerId = atomWithReset('');

export type ConsumerRowDetails = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobilePhoneNumber: string;
  availableAmount: number;
  status: Omit<Status, 'AUTHORIZE' | 'SETTLE'>;
  updated: string;
};
const ViewedConsumerRowDetails = atom<ConsumerRowDetails | null>(null);

const ConsumerAccounts = atom(async (get) => {
  const refresher = get(AccountsRefresher);
  const consumerId = get(ConsumerId);
  const storeId = await get(StoreId);
  const { peerId: merchantId } = await get(appDataByMerchant);
  const otbEnabled = await get(OtbSupportForWaterfall);
  if (!merchantId || !consumerId) return;
  if (otbEnabled && storeId) {
    try {
      await secureHttpClient.postGateway<unknown>(
        {
          fileName: 'customersTabManager.ts',
          fetchingFunction: 'ConsumerAccounts',
        },
        `v2/consumers/${consumerId}/accounts?storeId=${storeId}&posType=GenericMerchantDevice&channel=IN_STORE`,
        {},
      );
    } catch (e) {
      // prevent throw
    }
  }

  try {
    const accountsData =
      await secureHttpClient.getOperate<ConsumerAccountsData>(
        {
          fileName: 'customersTabManager.ts',
          fetchingFunction: 'ConsumerAccounts',
        },
        `api/consumers/${consumerId}/merchants/${merchantId}/accounts-data`,
      );

    return accountsData;
  } catch (e) {
    throw e;
  }
});

const AccountData = atom(async (get) => {
  const accountsData = await get(ConsumerAccounts);
  const account = accountsData?.selectedAccount;
  return account;
});

const ValidAccounts = atom(async (get) => {
  const validAccounts = (await get(ConsumerAccounts))?.validAccounts;
  return validAccounts;
});

const SelectedConsumerStatus = atomWithReset<Status | undefined>(undefined);

export const useGetSignedApplicationId = () => {
  return async (applicationId?: string) => {
    if (!applicationId) return;
    if (applicationId.includes('-')) return applicationId;

    try {
      const { applicationId: signedApplicationId } =
        await secureHttpClient.getOperate<{ applicationId: string }>(
          {
            fileName: 'customersTabManager.ts',
            fetchingFunction: 'useGetSignedApplicationId',
          },
          `api/applications/${applicationId}/sign`,
        );
      addToContext('signedApplicationId', signedApplicationId);
      return signedApplicationId;
    } catch (e) {
      return;
    }
  };
};

export const useGetSelectedConsumerStatus = () =>
  useAtomValue(SelectedConsumerStatus);
export const useSetSelectedConsumerStatus = () =>
  useSetAtom(SelectedConsumerStatus);
export const useResetSelectedConsumerStatus = () =>
  useResetAtom(SelectedConsumerStatus);

export const useGetConsumerAccounts = () =>
  useAtomValue(ConsumerAccounts)?.accounts;

export const useGetAccountData = (prop: keyof ConsumerAccountData) =>
  useAtomValue(AccountData)?.[prop];

export const useGetValidAccounts = () => useAtomValue(ValidAccounts);

export const useGetConsumerId = () => useAtomValue(ConsumerId);
export const useSetConsumerId = () => useSetAtom(ConsumerId);
export const useResetConsumerId = () => useResetAtom(ConsumerId);
export const useGetViewedConsumerRowDetails = () =>
  useAtomValue(ViewedConsumerRowDetails);
export const useSetViewedConsumerRowDetails = () =>
  useSetAtom(ViewedConsumerRowDetails);

export const useConsumerData = () => {
  return async (consumerId: string) => {
    try {
      const { consumer } = await secureHttpClient.getOperate<{
        consumer: ConsumerDetails & {
          merchantInteractions: object;
          updated: string;
        };
      }>(
        {
          fileName: 'customersTabManager.ts',
          fetchingFunction: 'useConsumerData',
        },
        `api/consumers/${consumerId}`,
      );
      if (!consumer) throw new Error('no consumer data returned');
      const consumerDetails = {
        id: consumer.id,
        firstName: consumer.firstName,
        lastName: consumer.lastName,
        email: consumer.email,
        mobilePhoneNumber: consumer.mobilePhoneNumber,
        billingAddress: consumer.billingAddress,
        shippingAddress: consumer.shippingAddress,
      };
      saveToUserSession('consumerDetails', consumerDetails);
      addToContext('consumer', consumerDetails);
      return consumerDetails;
    } catch (e) {
      logger.error(
        `Get consumer data exception occurred: ${JSON.stringify(e)}`,
        {
          filename: 'customersTabManager.ts',
          function: 'useConsumerData',
        },
      );
      return;
    }
  };
};

const AccountsRefresher = atom(0);

export const useSetAccountsRefresher = () => useSetAtom(AccountsRefresher);

const ConsumersListRefresher = atom('');

export const useSetConsumersListRefresher = () =>
  useSetAtom(ConsumersListRefresher);

export const useGetConsumersListRefresher = () =>
  useAtomValue(ConsumersListRefresher);

export const useHandlePendingAmountInterval = () => {
  const updateRefresher = useSetPendingAmountRefresher();
  useEffect(() => {
    const INTERVAL_DURATION = 1000 * 60 * 5;
    const timer = setInterval(() => {
      updateRefresher((n) => n + 1);
    }, INTERVAL_DURATION);
    return () => clearInterval(timer);
  }, []);
};
const PendingAmountRefresher = atom(0);

export const useSetPendingAmountRefresher = () => {
  return useSetAtom(PendingAmountRefresher);
};

const PendingAmountFetcher = atom(async (get) => {
  const merchantId = (await get(appDataByMerchant)).peerId;
  const storeId = await get(StoreId);
  const refresher = get(PendingAmountRefresher);
  try {
    const count = await secureHttpClient.getOperate<number>(
      {
        fileName: 'customersTabManager.ts',
        fetchingFunction: 'PendingAmountFetcher',
      },
      `api/consumers/count?merchantid=${merchantId}&accountstatuses=pendingconsumer&accountstatuses=checkoutinprogress${
        storeId ? `&storeId=${storeId}` : ''
      }`,
    );
    return count;
  } catch (e) {
    return 0;
  }
});

export const useGetPendingAmount = () => {
  return useAtomValue(PendingAmountFetcher);
};
